import { useMemo } from 'react';
import classNames from 'classnames';

import { LotStatus, Rights } from 'src/constants';
import { useLocalization } from 'src/contexts/localization-context';
import { formatCents } from 'src/utils';
import type { BankInteriorGridProps } from './bank-interior-grid.props';

import './bank-interior-grid.scss';

const MIN_TILE_SIZE = 89;

const STATUS_CLASS: Partial<Record<LotStatus, string>> = {
  [LotStatus.RESERVED]: 'bank-interior-grid__cell_reserved',
  [LotStatus.SOLD]: 'bank-interior-grid__cell_sold',
  [LotStatus.UNAVAILABLE]: 'bank-interior-grid__cell_hold',
};

const BankInteriorGrid = ({
  className,
  height,
  lots,
  selectedLot,
  onLotClick,
}: BankInteriorGridProps) => {
  const { getLocalization } = useLocalization();

  const { columns, rows } = useMemo(() => {
    let maxCol = 0;
    let maxRow = 0;

    lots.forEach(({ scheme }) => {
      if (scheme) {
        maxCol = Math.max(maxCol, scheme.x + scheme.width);
        maxRow = Math.max(maxRow, scheme.y + scheme.height);
      }
    });

    return { columns: maxCol || 1, rows: maxRow || 1 };
  }, [lots]);

  return (
    <section className={classNames('bank-interior-grid', className)} style={{ height }}>
      <div
        className="bank-interior-grid__grid"
        style={{
          gridTemplateColumns: `repeat(${columns}, minmax(${MIN_TILE_SIZE}px, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(${MIN_TILE_SIZE}px, auto))`,
        }}
      >
        {lots.map((lot) => {
          const statusClass = lot.status ? STATUS_CLASS[lot.status] ?? '' : '';

          return (
            <div
              key={lot.id}
              className={classNames(
                'bank-interior-grid__cell',
                lot.status !== LotStatus.AVAILABLE_FOR_SALE &&
                  'bank-interior-grid__cell_unavailable',
                statusClass
              )}
              style={
                lot.scheme
                  ? {
                      gridColumn: `${lot.scheme.x + 1} / span ${lot.scheme.width}`,
                      gridRow: `${lot.scheme.y + 1} / span ${lot.scheme.height}`,
                    }
                  : undefined
              }
            >
              <button
                className={classNames(
                  'bank-interior-grid__item',
                  selectedLot?.id === lot.id && 'bank-interior-grid__item_selected'
                )}
                aria-label={`${getLocalization('Lot')} ${lot.number}`}
                aria-pressed={selectedLot?.id === lot.id}
                disabled={lot.status === LotStatus.UNAVAILABLE}
                type="button"
                onClick={() => onLotClick(lot)}
              >
                <span className="bank-interior-grid__item-title">{lot.number}</span>
                <span className="bank-interior-grid__item-meta">{Rights[lot.rights]}</span>
                <span className="bank-interior-grid__item-price">
                  {lot.price ? formatCents(lot.price) : getLocalization('N/A')}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default BankInteriorGrid;
